import { Title, Radio, Tabs, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { BsActivity, BsClipboardData, BsClock, BsExclamationCircle, BsHeartPulse, BsPencil } from 'react-icons/bs';
import { MdHealing, MdHealthAndSafety } from 'react-icons/md';
import { useCreateRuleFormContext } from 'context/FormContext';
import { RuleFrequencies, RuleReportGenerations, RuleTrigger, RuleTriggers } from 'models/rules';
import { ReactNode } from 'react';
import { snakeCase } from 'lodash';
import { useDefineRuleFormValidations } from '../hooks/useDefineRuleFormValidations';
import { showUsaFeature } from '../../../../../utils/app';

const iconSize = 22;

export const RuleConfigurationFields = () => {
  const { t } = useTranslation();
  const { getInputProps, values, setFieldValue, clearErrors } = useCreateRuleFormContext();
  const { initialValues } = useDefineRuleFormValidations();

  const resetFields = (event: React.MouseEvent<HTMLButtonElement>) => {
    const textContent = (event.target as HTMLElement)?.textContent;

    if (textContent === t('time_since_prescription_label')) {
      setFieldValue('frequency', RuleFrequencies.prescription);
    }

    setFieldValue('data', initialValues.data);
    clearErrors();
  };

  const renderTab = (value: RuleTrigger, icon: ReactNode) => {
    return (
      <Tabs.Tab
        value={value}
        leftSection={icon}
        bd={'1px solid blue'}
        onClick={resetFields}
        disabled={values.formDisabled}
      >
        {t(`${snakeCase(value)}_label`)}
      </Tabs.Tab>
    );
  };

  const ruleConfigurationTrigger = Object.values(RuleReportGenerations).includes(values.trigger)
    ? RuleTriggers.reportGenerated
    : values.trigger;

  return (
    <>
      <Title order={4} mb={-10} mt={'xs'}>
        {t('rule_configuration_label')}
      </Title>
      <Radio.Group label={t('type_label')} description={t('rule_configuration_description')}>
        <Tabs
          variant={'pills'}
          mt={5}
          color={'var(--mantine-color-turquoise-3)'}
          {...getInputProps('trigger')}
          value={ruleConfigurationTrigger}
        >
          <Tabs.List justify={'space-between'} grow>
            {renderTab(RuleTriggers.heartRhythmTransition, <BsActivity size={iconSize} color={'purple'} />)}
            {renderTab(RuleTriggers.heartRhythmMeasurement, <BsHeartPulse size={iconSize} color={'red'} />)}
            {!showUsaFeature &&
              renderTab(RuleTriggers.physiologicalParameterChange, <MdHealing size={iconSize} color={'purple'} />)}
            {!showUsaFeature &&
              renderTab(
                RuleTriggers.physiologicalParameterThreshold,
                <MdHealthAndSafety size={iconSize} color={'var(--mantine-color-pink-8)'} />,
              )}
            {renderTab(
              RuleTriggers.physiologicalParameterMissing,
              <BsExclamationCircle size={iconSize} color={'orange'} />,
            )}
            {renderTab(
              RuleTriggers.heartRhythmNote,
              <BsPencil size={iconSize} color={'var(--mantine-color-blue-5)'} />,
            )}
            {renderTab(
              RuleTriggers.reportGenerated,
              <BsClipboardData size={iconSize} color={'var(--mantine-color-blue-5)'} />,
            )}
            {renderTab(RuleTriggers.timeSincePrescription, <BsClock size={iconSize} color={'green'} />)}
          </Tabs.List>
          <Text c={'blue'} size={'sm'} mt={'xs'}>
            {t(`${snakeCase(ruleConfigurationTrigger)}_text`)}
          </Text>
        </Tabs>
      </Radio.Group>
    </>
  );
};
